<template>
  <div class="form-user">
    <form class="form-user__form" @submit.prevent="handleSubmit">
      <section class="form-section" aria-labelledby="section-identity">
        <h3 id="section-identity" class="form-section__title">Identité</h3>
        <div class="form-grid form-grid--2">
          <div class="form-group">
            <label for="first_name">Prénom</label>
            <input
              id="first_name"
              v-model="formData.first_name"
              type="text"
              required
              class="form-control"
              autocomplete="given-name"
            />
          </div>
          <div class="form-group">
            <label for="last_name">Nom</label>
            <input
              id="last_name"
              v-model="formData.last_name"
              type="text"
              required
              class="form-control"
              autocomplete="family-name"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="email">Email</label>
          <input
            id="email"
            v-model="formData.email"
            type="email"
            required
            class="form-control"
            autocomplete="email"
          />
        </div>
      </section>

      <section class="form-section" aria-labelledby="section-account">
        <h3 id="section-account" class="form-section__title">Compte</h3>
        <div class="form-grid form-grid--2">
          <div class="form-group">
            <label for="username">Nom d'utilisateur</label>
            <input
              id="username"
              v-model="formData.username"
              type="text"
              required
              class="form-control"
              :class="{ 'is-invalid': usernameError }"
              placeholder="ex. jeandupont"
              autocomplete="username"
              @input="validateUsername"
            />
            <small class="text-muted">Lettres et chiffres uniquement, 3 caractères minimum.</small>
            <div v-if="usernameError" class="invalid-feedback d-block">{{ usernameError }}</div>
          </div>
          <div class="form-group">
            <label for="password">Mot de passe</label>
            <input
              id="password"
              v-model="formData.password"
              type="password"
              required
              class="form-control"
              :class="{ 'is-invalid': !!passwordError }"
              autocomplete="new-password"
              @input="validatePassword"
            />
            <small class="text-muted">Au moins 8 caractères, une lettre et un chiffre.</small>
            <div v-if="passwordError" class="invalid-feedback d-block">{{ passwordError }}</div>
          </div>
        </div>
      </section>

      <section class="form-section" aria-labelledby="section-role">
        <h3 id="section-role" class="form-section__title">Profil</h3>
        <div class="form-group">
          <label for="role">Type d'utilisateur</label>
          <select id="role" v-model="formData.role" class="form-control">
            <option value="employe">Employé</option>
            <option value="admin">Administrateur</option>
          </select>
          <small class="text-muted">Les administrateurs ont un accès complet au back-office.</small>
        </div>

        <div v-if="formData.role === 'employe'" class="form-group">
          <label for="role_employe">Typologie employé</label>
          <select id="role_employe" v-model="formData.role_employe" class="form-control">
            <option value="conseiller">Conseiller</option>
            <option value="it">IT / technique</option>
          </select>
          <small class="text-muted">
            Le détail des accès (menus, routes) est défini par les permissions ci-dessous.
          </small>
        </div>
      </section>

      <section
        v-if="formData.role === 'employe'"
        class="form-section form-section--perms"
        aria-labelledby="section-perms"
      >
        <h3 id="section-perms" class="form-section__title">Permissions back-office</h3>
        <p class="form-section__hint">
          Cochez les habilitations correspondant au poste. Sans au moins une permission, le compte ne pourra pas accéder au back-office.
        </p>
        <div class="perm-grid" role="group" aria-label="Permissions">
          <label v-for="opt in staffPermissionOptions" :key="opt.value" class="perm-check">
            <input v-model="formData.permissions" type="checkbox" :value="opt.value" />
            <span class="perm-check__text">
              <span class="perm-check__label">{{ opt.label }}</span>
              <span class="perm-check__desc">{{ opt.description }}</span>
            </span>
          </label>
        </div>
      </section>

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success" role="status">
        {{ successMessage }}
      </div>

      <div class="form-actions">
        <button
          type="submit"
          :disabled="loading || !!usernameError || !!passwordError"
          class="btn btn-primary"
        >
          <span v-if="loading" class="me-2" aria-hidden="true">
            <span class="spinner-border spinner-border-sm" role="status" />
          </span>
          {{ loading ? 'Création en cours…' : "Créer l'utilisateur" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { formatDjangoValidationErrors } from '@/utils/apiErrors';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';
import { clearAuthenticatedSession, isAuthenticatedSession } from '@/utils/sessionAuth';

const createUserSubmitGuard = createSubmitGuard(3000);

const TOKEN_KEYS = {
  ACCESS: 'auth_session',
  REFRESH: null /* httpOnly cookie */,
  ROLE: 'user_role',
};

/** Aligné sur le back-end (_STAFF_PERMISSION_KEYS / Employe.permissions). */
const STAFF_PERMISSION_OPTIONS = [
  {
    value: 'voir',
    label: 'Voir les analyses',
    description: 'Accès lecture aux dossiers et analyses en validation.',
  },
  {
    value: 'lecture_seule_global',
    label: 'Lecture étendue',
    description: 'Vue élargie sur les contenus (selon règles métier).',
  },
  {
    value: 'valider',
    label: 'Valider / générer',
    description: 'Validation et génération des analyses, requêtes clients.',
  },
  {
    value: 'conformite',
    label: 'Conformité & KYC',
    description: 'NameScan, signatures, validation paiement / KYC.',
  },
  {
    value: 'gestion_utilisateurs',
    label: 'Gestion utilisateurs & stats',
    description: 'Création de comptes staff, statistiques.',
  },
];

export default {
  name: 'FormUserBackoffice',
  watch: {
    'formData.role'(value) {
      if (value === 'admin') {
        this.formData.permissions = [];
      }
    },
  },
  data() {
    return {
      staffPermissionOptions: STAFF_PERMISSION_OPTIONS,
      formData: {
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'employe',
        role_employe: 'conseiller',
        permissions: [],
        est_admin: false,
        type_client: '',
      },
      loading: false,
      errorMessage: '',
      successMessage: '',
      usernameError: '',
      passwordError: '',
    };
  },
  methods: {
    validateUsername() {
      const v = this.formData.username || '';
      if (!v) {
        this.usernameError = '';
        return;
      }
      if (!/^[a-zA-Z0-9]+$/.test(v)) {
        this.usernameError = "Lettres et chiffres uniquement (pas d'espaces ni caractères spéciaux).";
      } else if (v.length < 3) {
        this.usernameError = "Le nom d'utilisateur doit contenir au moins 3 caractères.";
      } else {
        this.usernameError = '';
      }
    },
    validatePassword() {
      const p = this.formData.password || '';
      if (!p) {
        this.passwordError = '';
        return;
      }
      if (p.length < 8) {
        this.passwordError = 'Au moins 8 caractères.';
        return;
      }
      if (!/[0-9]/.test(p)) {
        this.passwordError = 'Au moins un chiffre.';
        return;
      }
      if (!/[a-zA-Z]/.test(p)) {
        this.passwordError = 'Au moins une lettre.';
        return;
      }
      this.passwordError = '';
    },
    async handleSubmit() {
      this.validateUsername();
      this.validatePassword();
      if (this.usernameError || this.passwordError) return;

      const guarded = await createUserSubmitGuard.run(async () => {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        if (!isAuthenticatedSession()) {
          throw new Error('Token non trouvé');
        }

        const dataToSend = {
          username: this.formData.username.trim(),
          first_name: this.formData.first_name.trim(),
          last_name: this.formData.last_name.trim(),
          email: this.formData.email.trim().toLowerCase(),
          password: this.formData.password,
          role: this.formData.role,
          est_admin: this.formData.role === 'admin',
        };
        if (this.formData.role === 'employe') {
          dataToSend.role_employe = this.formData.role_employe;
          dataToSend.permissions = [...this.formData.permissions];
        }

        const response = await axios.post('/api/admin/utilisateurs/create/', dataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.status === 201) {
          this.successMessage = 'Utilisateur créé avec succès.';
          this.resetForm();
          this.$bus.emit('refresh-employes');
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }
      } catch (error) {
        console.error("Erreur lors de la création de l'utilisateur:", error);
        const throttleMsg = formatThrottleMessage(error);
        if (throttleMsg) {
          this.errorMessage = throttleMsg;
        } else if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (typeof errorData === 'object') {
            this.errorMessage = formatDjangoValidationErrors(errorData) || 'Erreur de validation.';
          } else {
            // Jamais le texte brut renvoyé par le serveur (peut être une page
            // d'erreur HTML ou un message technique non destiné au client).
            this.errorMessage = "Erreur lors de la création de l'utilisateur.";
          }
        } else if (error.response) {
          switch (error.response.status) {
            case 400:
              this.errorMessage = 'Données invalides. Vérifiez les champs.';
              break;
            case 401:
              this.errorMessage = 'Session expirée, veuillez vous reconnecter.';
              clearAuthenticatedSession();
              /* refresh cookie cleared server-side */;
              localStorage.removeItem(TOKEN_KEYS.ROLE);
              Swal.fire(
                'Erreur',
                'Session expirée ou non autorisé. Veuillez vous reconnecter.',
                'error',
              );
              break;
            case 403:
              this.errorMessage = 'Accès non autorisé.';
              break;
            default:
              this.errorMessage = "Erreur lors de la création de l'utilisateur.";
          }
        } else if (error.request) {
          this.errorMessage = 'Impossible de contacter le serveur.';
        } else {
          this.errorMessage = 'Une erreur est survenue.';
        }
      } finally {
        this.loading = false;
      }
      });
      if (!guarded.ok) {
        this.errorMessage = 'Veuillez patienter avant une nouvelle création.';
      }
    },

    resetForm() {
      this.formData = {
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'employe',
        role_employe: 'conseiller',
        permissions: [],
        est_admin: false,
        type_client: '',
      };
      this.usernameError = '';
      this.passwordError = '';
    },
  },
};
</script>

<style scoped>
.form-user {
  padding: 0.25rem 0 1rem;
  font-family: var(--font-body);
  color: var(--bank-gray-800);
}

.form-user__form {
  max-width: 640px;
}

.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--bank-gray-200);
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section__title {
  margin: 0 0 0.75rem;
  font-family: var(--font-title);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--bank-navy);
  letter-spacing: var(--heading-letter-tight, -0.015em);
}

.form-section__hint {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: var(--bank-gray-600);
  line-height: 1.45;
}

.form-section--perms {
  background: color-mix(in srgb, var(--bank-navy) 4%, var(--bank-white));
  margin-left: -4px;
  margin-right: -4px;
  padding: 1rem 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--bank-navy) 10%, var(--bank-gray-200));
}

.form-grid {
  display: grid;
  gap: 0.75rem 1rem;
}

@media (min-width: 520px) {
  .form-grid--2 {
    grid-template-columns: 1fr 1fr;
  }
}

.form-group {
  margin-bottom: 0.75rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.35rem;
  color: var(--bank-gray-700);
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  margin-top: 2px;
  border: 1px solid var(--bank-gray-300);
  border-radius: 10px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  background: var(--bank-white);
  color: var(--bank-gray-900);
}

.form-control:focus {
  border-color: color-mix(in srgb, var(--bank-gold) 55%, var(--bank-gray-400));
  box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--bank-gold) 25%, transparent);
  outline: none;
}

.form-control.is-invalid {
  border-color: var(--bank-danger);
}

.form-control.is-invalid:focus {
  box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--bank-danger) 22%, transparent);
}

.perm-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.perm-check {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  background: var(--bank-white);
  border: 1px solid var(--bank-gray-200);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.perm-check:hover {
  border-color: color-mix(in srgb, var(--bank-gold) 35%, var(--bank-gray-300));
  box-shadow: 0 8px 22px rgba(10, 26, 47, 0.05);
}

.perm-check input {
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.perm-check__text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.perm-check__label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--bank-navy);
}

.perm-check__desc {
  font-size: 0.8rem;
  color: var(--bank-gray-600);
  line-height: 1.35;
}

.form-actions {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

.form-user :deep(.btn-primary) {
  padding: 10px 22px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--bank-navy) 0%, var(--bank-primary-light) 100%);
  color: var(--bank-white);
  box-shadow: 0 10px 24px rgba(10, 26, 47, 0.18);
  border: 1px solid color-mix(in srgb, var(--bank-gold) 35%, transparent);
}

.form-user :deep(.btn-primary:hover:not(:disabled)) {
  filter: brightness(1.05);
  border-color: color-mix(in srgb, var(--bank-gold) 55%, transparent);
  box-shadow: 0 14px 30px rgba(10, 26, 47, 0.22);
}

.form-user :deep(.btn-primary:disabled) {
  opacity: 0.55;
  cursor: not-allowed;
  filter: none;
}

.alert {
  padding: 10px 14px;
  margin: 10px 0;
  border-radius: 10px;
  font-size: 0.9rem;
}

.alert-danger {
  background: color-mix(in srgb, var(--bank-danger) 10%, var(--bank-white));
  color: var(--bank-danger);
  border: 1px solid color-mix(in srgb, var(--bank-danger) 28%, var(--bank-white));
}

.alert-success {
  background: color-mix(in srgb, var(--bank-success) 10%, var(--bank-white));
  color: color-mix(in srgb, var(--bank-success) 85%, var(--bank-navy));
  border: 1px solid color-mix(in srgb, var(--bank-success) 28%, var(--bank-white));
}

.text-muted {
  display: block;
  color: var(--bank-gray-500);
  font-size: 0.8125rem;
  margin-top: 0.35rem;
  line-height: 1.35;
}

.invalid-feedback {
  font-size: 0.8125rem;
  color: var(--bank-danger);
  margin-top: 0.35rem;
}
</style>
